/*
 * Cart drawer element (Dawn v16 base, Elexa group B changes):
 *  - header cart icon opens the drawer through a delegated listener (survives header re-renders);
 *  - overlay / Escape / close button close it, except in the theme editor with "Keep drawer open";
 *  - renderContents (used by Dawn's product form) renders through Elexa.cart.render, so the drawer
 *    renderer, progress animation and gift reconciliation are the same for every caller;
 *  - emits `elexa:cart-opened` { trigger } on open.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (evt) => {
      if (evt.target && evt.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    }
    if (CartDrawer.iconListenerBound) return;
    CartDrawer.iconListenerBound = true;
    document.addEventListener('click', (event) => {
      const link = event.target.closest('#cart-icon-bubble');
      const drawer = document.querySelector('cart-drawer');
      if (!link || !drawer) return;
      event.preventDefault();
      drawer.open(link);
    });
    document.addEventListener('keydown', (event) => {
      if (!event.code || event.code.toUpperCase() !== 'SPACE') return;
      const link = event.target.closest && event.target.closest('#cart-icon-bubble');
      const drawer = document.querySelector('cart-drawer');
      if (!link || !drawer) return;
      event.preventDefault();
      drawer.open(link);
    });
  }

  isKeptOpen() {
    return Boolean(window.Shopify && window.Shopify.designMode) && this.classList.contains('ex-cart--keep-open');
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else {
      // Opened by an add to cart (no trigger passed): focus goes back to the control the shopper
      // used (e.g. the add-to-cart button), not to an older trigger such as the header icon.
      const current = document.activeElement;
      this.setActiveElement(current && current !== document.body && !this.contains(current) ? current : null);
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // Trap focus once the panel has slid in. Only the panel's own transform transition counts: a late
    // transitionend from the previous close (e.g. a button's visibility transition) used to fire this
    // first, while the panel was still hidden, and focus stayed on the page. The timer covers reduced
    // motion (no transition at all).
    if (this.onOpenEnd) this.removeEventListener('transitionend', this.onOpenEnd);
    clearTimeout(this.openFocusTimer);
    this.onOpenEnd = (evt) => {
      const panel = this.querySelector('.drawer__inner');
      if (evt && (evt.target !== panel || evt.propertyName !== 'transform')) return;
      this.removeEventListener('transitionend', this.onOpenEnd);
      clearTimeout(this.openFocusTimer);
      if (!this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', this.onOpenEnd);
    this.openFocusTimer = setTimeout(() => this.onOpenEnd(), 700);

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();

    this.dispatchEvent(new CustomEvent('elexa:cart-opened', { bubbles: true, detail: { trigger: triggeredBy || null } }));
  }

  close() {
    if (this.isKeptOpen()) return;
    clearTimeout(this.openFocusTimer);
    if (this.onOpenEnd) this.removeEventListener('transitionend', this.onOpenEnd);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /** Called by Dawn's product form with the /cart/add.js response (sections included). */
  renderContents(parsedState) {
    this.productId = parsedState.id;
    const cartApi = window.Elexa && window.Elexa.cart;
    if (cartApi && typeof cartApi.render === 'function') {
      if (typeof cartApi.markExternalRender === 'function') cartApi.markExternalRender();
      cartApi.render(parsedState.sections, { seq: ++cartApi.seq, action: 'external-add' });
    } else {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    }

    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
